import React, { useContext } from "react"
import {
  Modal,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native"
import Loader from "../utility/loader"
import { textStyles } from "./style"
import { ThemeContext } from "../utility/context/ThemeContext"
import type { ThemeColors } from "./theme-types"
import Haptics from "../utility/haptics"
import { MAX_CONTENT_WIDTH } from "../config"
import ErrorAnimation from "../utility/error-animation"

export type DialogButtonRole = "default" | "destructive" | "cancel" | "confirm"

export type DialogOptionProps = {
  onPress: () => void
  title: string
  role: DialogButtonRole
  icon?: React.ReactElement
}

type Props = {
  options: DialogOptionProps[]
  visible: boolean
  title: string
  subMessage: string
  loading?: boolean
  onDismiss?: () => void
  backDropProps?: {
    backgroundColor?: string
    onPress?: () => void
  }
  errorAnimation?: boolean
}

function getDialogOptionColor(role: DialogButtonRole, colors: ThemeColors): string {
  switch (role) {
    case "destructive":
      return colors.danger
    case "confirm":
      return colors.success
    case "cancel":
    case "default":
    default:
      return colors.textMain
  }
}

function ThemedDialog({
  options,
  visible,
  title,
  subMessage,
  loading,
  onDismiss,
  backDropProps,
  errorAnimation,
}: Props): React.ReactElement<Props> {
  const theme = useContext(ThemeContext)
  const { borderColor, bgMain, textMain } = theme.theme.colors
  const { width: windowWidth } = useWindowDimensions()
  const dialogWidth = Math.min(MAX_CONTENT_WIDTH, windowWidth * 0.8)

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onDismiss={onDismiss}
      onRequestClose={backDropProps?.onPress || onDismiss}
    >
      <TouchableOpacity
        onPress={() => {
          backDropProps?.onPress?.()
          onDismiss?.()
        }}
        style={[styles.dialogBG, { backgroundColor: backDropProps?.backgroundColor || "rgba(0,0,0,0.5)" }]}
        activeOpacity={1}
      >
        <TouchableOpacity
          activeOpacity={1}
          style={[
            styles.dialogFG,
            { backgroundColor: bgMain, borderWidth: 1, borderColor, width: dialogWidth },
          ]}
        >
          {loading ? (
            <View style={{ padding: 16, alignItems: "center" }}>
              <Loader size="large" />
            </View>
          ) : (
            <>
              <View style={{ padding: 16, gap: 10, cursor: "auto", alignItems: "center" }}>
                {errorAnimation ? <ErrorAnimation /> : null}
                <Text style={[textStyles.h3, { color: textMain, textAlign: "center" }]}>{title}</Text>
                <View style={{ gap: 5 }}>
                  <Text style={[textStyles.body, { color: textMain, textAlign: "center" }]}>{subMessage}</Text>
                </View>
              </View>

              <View style={{ width: "100%" }}>
                {options.map((option, idx) => {
                  const optionColor = getDialogOptionColor(option.role, theme.theme.colors)
                  const coloredIcon =
                    option.icon &&
                    React.cloneElement(option.icon as React.ReactElement<{ color?: string }>, {
                      color: optionColor,
                    })

                  return (
                    <TouchableOpacity
                      key={idx}
                      onPress={() => {
                        Haptics.selection()
                        option.onPress()
                      }}
                      style={{ width: "100%" }}
                    >
                      <View style={[styles.dialogButton, { borderTopColor: borderColor }]}>
                        {coloredIcon ? <View style={styles.iconWrapper}>{coloredIcon}</View> : null}
                        <Text style={[textStyles.body, { textAlign: "center", color: optionColor }]}>
                          {option.title}
                        </Text>
                      </View>
                    </TouchableOpacity>
                  )
                })}
              </View>
            </>
          )}
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  )
}

export default ThemedDialog

const styles = StyleSheet.create({
  dialogBG: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  dialogFG: {
    minWidth: 250,
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "stretch",
    borderRadius: 10,
  },
  dialogButton: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    borderTopWidth: 1,
    padding: 16,
  },
  iconWrapper: {
    justifyContent: "center",
    alignItems: "center",
    marginRight: 5,
  },
})
